import { makeStyles, styled } from '@material-ui/styles'
import { retypeVolumeDocumentation } from 'app/constants'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import PicklistField from 'core/components/validatedForm/DropdownField'
import Info from 'core/components/validatedForm/Info'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import React, { useCallback } from 'react'
import VolumeTypesPicklist from '../volume-types/VolumeTypesPicklist'
import { retypeVolume } from './actions'

const Container = styled('div')({
  gap: 16,
  display: 'grid',
})

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  warning: {
    display: 'grid',
    gap: 8,
  },
  icon: {
    marginRight: 8,
  },
  step: {
    display: 'grid',
    gridTemplateColumns: 'max-content auto',
    gap: 8,
  },
}))

export default function RetypeVolumeDialog({ rows: [volume], onClose }) {
  const isInUse = volume?.status === 'in-use'
  const classes = useStyles()
  const { params, getParamsUpdater } = useParams({
    volumeType: '',
    confirmBypass: false,
  })
  const { update, updating, error, reset } = useUpdateAction(retypeVolume)

  const handleSubmit = useCallback(async () => {
    const body = {
      'os-retype': {
        new_type: params.volumeType,
        migration_policy: 'on-demand',
      },
    }
    const { success } = await update({
      id: volume?.id,
      body,
    })
    if (success) handleClose()
  }, [volume, params])

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <ModalForm
      title="Retype Volume"
      onSubmit={handleSubmit}
      onClose={onClose}
      submitting={updating}
      error={error}
      submitTitle={`Retype Volume`}
      panel="dialog"
      maxWidth={800}
      disableSubmit={isInUse ? !params.confirmBypass : false}
      open
    >
      <ServiceUnhealthyInfo action="Volume Retype" blockStorage />
      <Container>
        <Text variant="body2">
          The retype operation enables migration of a volume from one storage type to another.
        </Text>
        <Info error>
          <div className={classes.warning}>
            <Text variant="body2">
              <FontAwesomeIcon className={classes.icon}>triangle-exclamation</FontAwesomeIcon>
              <b>WARNING</b>
            </Text>
            {isInUse ? (
              <Text variant="body2">
                The migration of an <b>in-use</b> volume (live migration) is driver dependent and
                may not succeed due to limitations of the source or destination volume driver.
              </Text>
            ) : (
              <Text variant="body2">
                Migration of an <b>available</b> volume (cold migration) is driver dependent and may
                not succeed due to limitations of the source or destination volume driver.
              </Text>
            )}
            <Text variant="body2">
              <ExternalLink url={retypeVolumeDocumentation}>
                Read this before you perform the operation
              </ExternalLink>
            </Text>
          </div>
        </Info>
        <Text variant="body2">Select a new volume type to retype this volume to.</Text>
        <PicklistField
          DropdownComponent={VolumeTypesPicklist}
          id="volumeType"
          onChange={getParamsUpdater('volumeType')}
          value={params.volumeType}
          label="Volume Type"
          required
        />
        {isInUse && (
          <CheckboxField
            id="confirmBypass"
            label="I understand the risks of retyping an in-use volume and would like to proceed forward"
            value={params.confirmBypass}
            onChange={getParamsUpdater('confirmBypass')}
            required
          />
        )}
      </Container>
    </ModalForm>
  )
}
